/**
 * Financial Profile Hook
 *
 * Custom hook for managing consumer financial profile data including
 * employment, assets, and liabilities.
 */

import { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { Asset, EmploymentInfo, FinancialProfile, Liability } from '@/lib/constants/financial-profile';
import { useProfile, useUpdateProfile } from './useProfile';

/**
 * Computed financial totals
 */
export interface FinancialTotals {
  totalAssets: number;
  totalDebt: number;
  monthlyPayments: number;
  netWorth: number;
}

/**
 * Hook for managing consumer financial profile
 *
 * Provides CRUD operations for employment info, assets, and liabilities
 * with automatic totals calculation.
 */
export function useFinancialProfile() {
  const { data: profile, isPending } = useProfile();
  const updateProfileMutation = useUpdateProfile();

  // Extract financial profile from the nested profile.profile field
  const financialProfile = useMemo<FinancialProfile>(() => {
    const raw = profile?.profile as Record<string, unknown> | undefined;
    const fp = raw?.financialProfile as FinancialProfile | undefined;

    return {
      employment: fp?.employment,
      assets: fp?.assets ?? [],
      liabilities: fp?.liabilities ?? [],
      lastUpdated: fp?.lastUpdated,
    };
  }, [profile]);

  // Local edit state
  const [editedProfile, setEditedProfile] = useState<FinancialProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Get current data (edited or saved)
  const currentProfile = editedProfile ?? financialProfile;

  // ============================================================================
  // EMPLOYMENT
  // ============================================================================

  const updateEmployment = useCallback(
    (employment: Partial<EmploymentInfo>) => {
      setEditedProfile((prev) => ({
        ...(prev ?? financialProfile),
        employment: {
          ...(prev?.employment ?? financialProfile.employment ?? {}),
          ...employment,
        } as EmploymentInfo,
      }));
    },
    [financialProfile],
  );

  // ============================================================================
  // ASSETS
  // ============================================================================

  const addAsset = useCallback(
    (asset: Omit<Asset, 'id'>) => {
      setEditedProfile((prev) => ({
        ...(prev ?? financialProfile),
        assets: [...(prev?.assets ?? financialProfile.assets), { ...asset, id: uuidv4() }],
      }));
    },
    [financialProfile],
  );

  const updateAsset = useCallback(
    (id: string, updates: Partial<Asset>) => {
      setEditedProfile((prev) => ({
        ...(prev ?? financialProfile),
        assets: (prev?.assets ?? financialProfile.assets).map((a) => (a.id === id ? { ...a, ...updates } : a)),
      }));
    },
    [financialProfile],
  );

  const removeAsset = useCallback(
    (id: string) => {
      setEditedProfile((prev) => ({
        ...(prev ?? financialProfile),
        assets: (prev?.assets ?? financialProfile.assets).filter((a) => a.id !== id),
      }));
    },
    [financialProfile],
  );

  // ============================================================================
  // LIABILITIES
  // ============================================================================

  const addLiability = useCallback(
    (liability: Omit<Liability, 'id'>) => {
      setEditedProfile((prev) => ({
        ...(prev ?? financialProfile),
        liabilities: [...(prev?.liabilities ?? financialProfile.liabilities), { ...liability, id: uuidv4() }],
      }));
    },
    [financialProfile],
  );

  const updateLiability = useCallback(
    (id: string, updates: Partial<Liability>) => {
      setEditedProfile((prev) => ({
        ...(prev ?? financialProfile),
        liabilities: (prev?.liabilities ?? financialProfile.liabilities).map((l) =>
          l.id === id ? { ...l, ...updates } : l,
        ),
      }));
    },
    [financialProfile],
  );

  const removeLiability = useCallback(
    (id: string) => {
      setEditedProfile((prev) => ({
        ...(prev ?? financialProfile),
        liabilities: (prev?.liabilities ?? financialProfile.liabilities).filter((l) => l.id !== id),
      }));
    },
    [financialProfile],
  );

  // ============================================================================
  // COMPUTED TOTALS
  // ============================================================================

  const totals = useMemo<FinancialTotals>(() => {
    const assets = currentProfile.assets ?? [];
    const liabilities = currentProfile.liabilities ?? [];

    const totalAssets = assets.reduce((sum, a) => sum + a.approximateValue, 0);
    const totalDebt = liabilities.reduce((sum, l) => sum + l.balance, 0);
    const monthlyPayments = liabilities.reduce((sum, l) => sum + l.monthlyPayment, 0);
    const netWorth = totalAssets - totalDebt;

    return {
      totalAssets,
      totalDebt,
      monthlyPayments,
      netWorth,
    };
  }, [currentProfile]);

  // ============================================================================
  // SAVE/CANCEL
  // ============================================================================

  const saveChanges = useCallback(async () => {
    if (!editedProfile) return;

    const existingProfile = (profile?.profile as Record<string, unknown>) ?? {};

    await updateProfileMutation.mutateAsync({
      profile: {
        ...existingProfile,
        financialProfile: {
          ...editedProfile,
          lastUpdated: new Date().toISOString(),
        },
      },
    });

    setEditedProfile(null);
    setIsEditing(false);
  }, [editedProfile, profile, updateProfileMutation]);

  const cancelChanges = useCallback(() => {
    setEditedProfile(null);
    setIsEditing(false);
  }, []);

  const startEditing = useCallback(() => {
    setEditedProfile(financialProfile);
    setIsEditing(true);
  }, [financialProfile]);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    financialProfile: currentProfile,
    isEditing,
    isPending,
    isSaving: updateProfileMutation.isPending,
    error: updateProfileMutation.error,
    totals,

    // Actions
    startEditing,
    saveChanges,
    cancelChanges,

    // Employment
    updateEmployment,

    // Assets
    addAsset,
    updateAsset,
    removeAsset,

    // Liabilities
    addLiability,
    updateLiability,
    removeLiability,
  };
}

export default useFinancialProfile;
